/**
 * Stripe Connect Service
 * Handles payment method management and Stripe Connect account setup
 */

import { loadStripe, Stripe, StripeElements, StripeCardElement } from '@stripe/stripe-js';
import apiDefaults from './api';
import { ApiResponse } from '../types';

export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account';
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    country: string;
  };
  bank_account?: {
    bank_name: string;
    last4: string;
    account_type: string;
    routing_number: string;
  };
  isDefault: boolean;
  createdAt: string;
}

export interface StripeConnectAccount {
  id: string;
  accountId: string;
  status: 'pending' | 'restricted' | 'enabled';
  requiresAction: boolean;
  actionUrl?: string;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
  capabilities: {
    transfers?: 'active' | 'inactive' | 'pending';
    card_payments?: 'active' | 'inactive' | 'pending';
  };
  createdAt: string;
  updatedAt: string;
}

export interface PaymentIntent {
  id: string;
  clientSecret: string;
  amount: number;
  currency: string;
  status: string;
  metadata?: { [key: string]: string };
}

class StripeService {
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;

  /**
   * Initialize Stripe
   */
  async initialize(): Promise<Stripe> {
    if (this.stripe) {
      return this.stripe;
    }

    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      throw new Error('Stripe publishable key not configured');
    }

    this.stripe = await loadStripe(publishableKey);
    if (!this.stripe) {
      throw new Error('Failed to load Stripe');
    }

    return this.stripe;
  }

  /**
   * Create Stripe Elements instance
   */
  async createElements(): Promise<StripeElements> {
    const stripe = await this.initialize();
    
    this.elements = stripe.elements({
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#D4A574', // Honey color
          colorBackground: '#ffffff',
          colorText: '#1f2937',
          colorDanger: '#ef4444',
          fontFamily: 'system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '6px',
        },
      },
    });

    return this.elements;
  }

  /**
   * Create Stripe Connect account for user
   */
  async createConnectAccount(data: {
    type: 'individual' | 'company';
    email: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    address?: {
      line1: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  }): Promise<ApiResponse<{ accountId: string; setupUrl: string }>> {
    try {
      const response = await apiDefaults.post('/stripe/connect/create-account', data);
      return response as ApiResponse<{ accountId: string; setupUrl: string }>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create Stripe Connect account',
        message: 'Failed to create Stripe Connect account'
      };
    }
  }

  /**
   * Get Stripe Connect account status
   */
  async getConnectAccountStatus(): Promise<ApiResponse<StripeConnectAccount>> {
    try {
      const response = await apiDefaults.get('/stripe/connect/account');
      return response as ApiResponse<StripeConnectAccount>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get account status',
        message: 'Failed to get account status'
      };
    }
  }

  /**
   * Create account link for Connect onboarding
   */
  async createAccountLink(): Promise<ApiResponse<{ url: string }>> {
    try {
      const response = await apiDefaults.post('/stripe/connect/account-link');
      return response as ApiResponse<{ url: string }>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create account link',
        message: 'Failed to create account link'
      };
    }
  }

  /**
   * Add payment method
   */
  async addPaymentMethod(paymentMethodId: string): Promise<ApiResponse<PaymentMethod>> {
    try {
      const response = await apiDefaults.post('/stripe/payment-methods', {
        payment_method_id: paymentMethodId
      });
      return response as ApiResponse<PaymentMethod>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to add payment method',
        message: 'Failed to add payment method'
      };
    }
  }

  /**
   * Get user's payment methods
   */
  async getPaymentMethods(): Promise<ApiResponse<PaymentMethod[]>> {
    try {
      const response = await apiDefaults.get('/stripe/payment-methods');
      return response as ApiResponse<PaymentMethod[]>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get payment methods',
        message: 'Failed to get payment methods'
      };
    }
  }

  /**
   * Set default payment method
   */
  async setDefaultPaymentMethod(paymentMethodId: string): Promise<ApiResponse> {
    try {
      const response = await apiDefaults.post(`/stripe/payment-methods/${paymentMethodId}/default`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to set default payment method',
        message: 'Failed to set default payment method'
      };
    }
  }

  /**
   * Remove payment method
   */
  async removePaymentMethod(paymentMethodId: string): Promise<ApiResponse> {
    try {
      const response = await apiDefaults.delete(`/stripe/payment-methods/${paymentMethodId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to remove payment method',
        message: 'Failed to remove payment method'
      };
    }
  }

  /**
   * Create payment intent for property purchase
   */
  async createPaymentIntent(data: {
    propertyId: string;
    shares: number;
    amount: number; // Amount in cents
  }): Promise<ApiResponse<PaymentIntent>> {
    try {
      const response = await apiDefaults.post('/stripe/payment-intents', data);
      return response as ApiResponse<PaymentIntent>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create payment intent',
        message: 'Failed to create payment intent'
      };
    }
  }

  /**
   * Confirm payment intent
   */
  async confirmPaymentIntent(
    clientSecret: string,
    paymentMethodId?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const stripe = await this.initialize();

      const result = await stripe.confirmPayment({
        clientSecret,
        confirmParams: {
          payment_method: paymentMethodId,
          return_url: `${window.location.origin}/payment/success`,
        },
      });

      if (result.error) {
        return {
          success: false,
          error: result.error.message || 'Payment failed'
        };
      }

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Payment confirmation failed'
      };
    }
  }

  /**
   * Create setup intent for adding payment method
   */
  async createSetupIntent(): Promise<ApiResponse<{ client_secret: string }>> {
    try {
      const response = await apiDefaults.post('/stripe/setup-intents');
      return response as ApiResponse<{ client_secret: string }>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create setup intent',
        message: 'Failed to create setup intent'
      };
    }
  }

  /**
   * Confirm setup intent for payment method
   */
  async confirmSetupIntent(
    clientSecret: string,
    cardElement: StripeCardElement
  ): Promise<{ success: boolean; paymentMethodId?: string; error?: string }> {
    try {
      const stripe = await this.initialize();

      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (result.error) {
        return {
          success: false,
          error: result.error.message || 'Setup failed'
        };
      }

      return {
        success: true,
        paymentMethodId: result.setupIntent.payment_method as string
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Setup confirmation failed'
      };
    }
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Convert from cents
  }

  /**
   * Validate card number
   */
  validateCard(cardNumber: string): {
    isValid: boolean;
    brand?: string;
    formatted?: string;
  } {
    // Remove all non-digits
    const cleaned = cardNumber.replace(/\D/g, '');
    
    // Check if empty
    if (!cleaned) {
      return { isValid: false };
    }

    // Luhn algorithm for validation
    let sum = 0;
    let isEven = false;

    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i]);

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    const isValid = sum % 10 === 0 && cleaned.length >= 13;

    // Detect card brand
    let brand = 'unknown';
    if (/^4/.test(cleaned)) brand = 'visa';
    else if (/^5[1-5]/.test(cleaned)) brand = 'mastercard';
    else if (/^3[47]/.test(cleaned)) brand = 'amex';
    else if (/^6/.test(cleaned)) brand = 'discover';

    // Format card number
    let formatted = cleaned;
    if (brand === 'amex') {
      formatted = cleaned.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
    } else {
      formatted = cleaned.replace(/(\d{4})/g, '$1 ').trim();
    }

    return {
      isValid,
      brand,
      formatted
    };
  }

  /**
   * Get card icon based on brand
   */
  getCardIcon(brand: string): string {
    const icons: { [key: string]: string } = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳',
      unknown: '💳'
    };
    return icons[brand] || icons.unknown;
  }

  /**
   * Check if Connect account setup is required
   */
  async requiresConnectSetup(): Promise<boolean> {
    try {
      const response = await this.getConnectAccountStatus();
      return !response.success || 
             response.data?.status !== 'enabled' || 
             response.data?.requiresAction === true;
    } catch (error) {
      console.error('Error checking Connect setup status:', error);
      return true;
    }
  }

  /**
   * Get payout schedule information
   */
  getPayoutSchedule(): {
    frequency: string;
    day: string;
    description: string;
  } {
    return {
      frequency: 'monthly',
      day: '1st',
      description: 'Dividend payouts are processed on the 1st of each month for the previous month\'s earnings.'
    };
  }

  /**
   * Initialize bank account linking via Stripe Connect
   */
  async initializeBankLinking(): Promise<ApiResponse<{ url: string; client_secret: string }>> {
    try {
      const response = await apiDefaults.post('/bank/link-session');
      return response as ApiResponse<{ url: string; client_secret: string }>;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to initialize bank linking'
      };
    }
  }
}

export const stripeService = new StripeService();
export default stripeService;
